// Graph normalization utilities (Stage 2 — step 1)
// Build a single source-of-truth adjacency from live canvas nodes/edges.
// This module is read-only; integration into export happens in subsequent steps.

/**
 * Build normalized graph structures from canvas nodes/edges.
 * - Filters out edges with missing source/target
 * - Computes adjacency lists
 * - Resolves canonical start node id (if present)
 * - Collects end node ids
 *
 * @param {Array} nodes
 * @param {Array} edges
 * @returns {{
 *   nodeSet: Set<string>,
 *   nodeById: Map<string, any>,
 *   edgesClean: Array<{ id?: string, source: string, target: string, data?: any }>,
 *   adjacency: Map<string, Array<{ target: string, edge: any }>>,
 *   reverse: Map<string, Array<{ source: string, edge: any }>>,
 *   startNodeId: string | null,
 *   endNodeIds: Set<string>
 * }}
 */
function buildNormalizedGraph(nodes = [], edges = []) {
  const nodeSet = new Set();
  const nodeById = new Map();
  for (const n of nodes) {
    if (!n || !n.id) continue;
    nodeSet.add(String(n.id));
    nodeById.set(String(n.id), n);
  }

  const edgesClean = [];
  for (const e of edges) {
    if (!e) continue;
    const source = String(e.source || '').trim();
    const target = String(e.target || '').trim();
    if (!source || !target) continue;
    if (!nodeSet.has(source) || !nodeSet.has(target)) continue; // purge stale
    edgesClean.push({ id: e.id, source, target, data: (e.data ? JSON.parse(JSON.stringify(e.data)) : undefined) });
  }

  const adjacency = new Map();
  const reverse = new Map();
  for (const e of edgesClean) {
    if (!adjacency.has(e.source)) adjacency.set(e.source, []);
    adjacency.get(e.source).push({ target: e.target, edge: e });
    if (!reverse.has(e.target)) reverse.set(e.target, []);
    reverse.get(e.target).push({ source: e.source, edge: e });
  }

  let startNodeId = null;
  for (const n of nodes) {
    if (n && n.type === 'start') { startNodeId = String(n.id); break; }
  }
  const endNodeIds = new Set(nodes.filter(n => n && n.type === 'end').map(n => String(n.id)));

  return { nodeSet, nodeById, edgesClean, adjacency, reverse, startNodeId, endNodeIds };
}

/**
 * Breadth-first traversal order from a given start id across existing nodes/edges.
 * Returns a list of node ids in first-appearance order.
 */
function bfsOrder(startId, adjacency) {
  const order = [];
  if (!startId) return order;
  const seen = new Set([startId]);
  const q = [startId];
  while (q.length) {
    const cur = q.shift();
    order.push(cur);
    const outs = adjacency.get(cur) || [];
    for (const { target } of outs) {
      if (!seen.has(target)) { seen.add(target); q.push(target); }
    }
  }
  return order;
}

/**
 * Normalize priority to a finite number. Defaults to 0 when invalid.
 */
function normalizePriority(p) {
  const n = Number(p);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Build normalized flow entries from cleaned edges with canonical START mapping.
 * NOTE: Sorting and export integration occur in a later Stage 2 step.
 */
function buildFlowFromGraph({ edgesClean, startNodeId }) {
  return edgesClean.map(e => ({
    from: (startNodeId && e.source === startNodeId) ? 'START' : e.source,
    to: e.target,
    condition: (e.data && typeof e.data.condition === 'string') ? e.data.condition : '',
    priority: normalizePriority(e.data && e.data.priority)
  }));
}

module.exports = { buildNormalizedGraph, bfsOrder, normalizePriority, buildFlowFromGraph };
